import React, { useContext, useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  PermMedia,
  EmojiEmotions,
  Addchart,
} from "@mui/icons-material";
import { AuthContext } from "../../context/AuthContext";
import { DarkModeContext } from "../../context/darkModeContext";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import sendIcon from "../../assets/icon/send.png";
import { renderFormattedText } from "../../utils/mentionFormatter";
import { authHeader } from "../../utils/authHeader";
import { API_BASE_URL, urlDeArchivo } from "../../config";
import CreatePollModal from "../polls/CreatePollModal";
import { ESPACIO_ABIERTO_ID, espaciosAdministrados } from "../../utils/permisosEspacio";
import "./share.scss";

const Share = () => {
  const { currentUser } = useContext(AuthContext);
  const { darkMode } = useContext(DarkModeContext);
  const { spaceId: routeSpaceId } = useParams();

  const [input, setInput] = useState("");
  const [files, setFiles] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState("");
  const [showEmojis, setShowEmojis] = useState(false);
  const [showCreatePoll, setShowCreatePoll] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionStart, setMentionStart] = useState(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionNotice, setMentionNotice] = useState("");
  const [isPosting, setIsPosting] = useState(false);
  const [postStatus, setPostStatus] = useState(""); // "loading", "success", "error"
  const [postMessage, setPostMessage] = useState("");
  const [videosLoaded, setVideosLoaded] = useState({}); // Rastrea qué videos están completamente cargados
  const [uploadProgress, setUploadProgress] = useState(0); // Progreso de subida 0-100

  const textareaRef = useRef();
  const mirrorRef = useRef();

  const isAdmin = currentUser?.rol === "admin";
  // Espacios de los que esta persona es encargada (vacío para la mayoría).
  const misEspacios = espaciosAdministrados(currentUser);

  // 1) Carga datos de usuario
  useEffect(() => {
    if (!currentUser?.id) return;
    fetch(`${API_BASE_URL}/api/users/${currentUser.id}`, {
      headers: { ...authHeader() },
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error("Error al obtener datos del usuario"))))
      .then(setUserInfo)
      .catch(console.error);
  }, [currentUser]);

  // 2) Carga espacios y asigna espacio según rol
  useEffect(() => {
    const fetchSpaces = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/spaces`, {
          headers: { ...authHeader() },
        });
        const { spaces } = await res.json();
        setSpaces(spaces);

        if (routeSpaceId) {
          setSelectedSpace(routeSpaceId);
        } else if (isAdmin) {
          const mine = spaces.find(s => s.user?.id === currentUser.id);
          setSelectedSpace(mine?.id || spaces[0]?.id || "1");
        } else {
          // Empleados y temporales publican en el muro general. Un encargado
          // también arranca ahí; sus espacios los elige en "Publicar en:".
          // Solo si el muro general no existe arranca en el primero suyo.
          const propios = espaciosAdministrados(currentUser);
          const hayMuroGeneral = spaces.some((s) => s.id === ESPACIO_ABIERTO_ID);
          const primeroPropio = spaces.find((s) => propios.includes(Number(s.id)));
          setSelectedSpace(
            String(hayMuroGeneral || !primeroPropio ? ESPACIO_ABIERTO_ID : primeroPropio.id)
          );
        }
      } catch (err) {
        console.error("Error cargando espacios:", err);
        if (!isAdmin) {
          setSelectedSpace(String(ESPACIO_ABIERTO_ID)); // fallback si falla la carga
        }
      }
    };

    fetchSpaces();
  }, [currentUser, routeSpaceId, isAdmin]);

  // Espacios que se ofrecen en "Publicar en:". El administrador, todos; un
  // encargado, el muro general más los suyos; el resto no ve el selector.
  const espaciosParaPublicar = isAdmin
    ? spaces
    : spaces.filter(
        (s) => s.id === ESPACIO_ABIERTO_ID || misEspacios.includes(Number(s.id))
      );
  const muestraSelector =
    !routeSpaceId && (isAdmin || misEspacios.length > 0) && espaciosParaPublicar.length > 0;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/users`, {
          headers: { ...authHeader() },
        });
        if (!res.ok) return;
        const users = await res.json();
        setAllUsers(Array.isArray(users) ? users : []);
      } catch (error) {
        console.error("Error cargando usuarios para menciones:", error);
      }
    };

    fetchUsers();
  }, []);

  if (!currentUser || !userInfo || !selectedSpace) {
    return <div className="shareLoading">Cargando componente Share...</div>;
  }

  // 3) Publicar post
  const handlePost = async (e) => {
    // Prevenir comportamiento predeterminado si se llama desde un evento
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!input.trim() && files.length === 0) {
      setPostStatus("error");
      setPostMessage("Escribe algo o adjunta un archivo para publicar.");
      setTimeout(() => setPostStatus(""), 3000);
      return;
    }

    // Prevenir múltiples clics
    if (isPosting) {
      return;
    }

    setPostStatus("loading");
    setPostMessage(files.length > 0 ? "Preparando archivos..." : "Publicando...");
    setUploadProgress(0);
    setIsPosting(true);

    const fd = new FormData();
    fd.append("uid", userInfo.id);
    fd.append("displayName", userInfo.name);
    fd.append("content", input);
    fd.append("spaceId", selectedSpace);
    files.forEach(f => fd.append("media", f));

    try {
      const totalUploadBytes = files.reduce((sum, file) => sum + (file?.size || 0), 0);
      
      // Timeout basado en tamaño del archivo con márgenes muy generosos
      // Calculamos basado en 1MB/s como velocidad mínima (muy conservador)
      let requestTimeoutMs;
      if (totalUploadBytes === 0) {
        requestTimeoutMs = 30000; // 30 segundos para archivos sin medios
      } else {
        const estimatedSeconds = Math.ceil(totalUploadBytes / (1024 * 1024)); // 1MB/s
        const processingOverhead = 120000; // 2 minutos de overhead para procesamiento servidor
        requestTimeoutMs = Math.max(
          10 * 60 * 1000, // Mínimo 10 minutos
          (estimatedSeconds * 1000) + processingOverhead
        );
        // Máximo nunca supere 45 minutos
        requestTimeoutMs = Math.min(requestTimeoutMs, 45 * 60 * 1000);
      }

      // Usar XMLHttpRequest para rastrear progreso de subida
      const xhr = new XMLHttpRequest();
      let lastProgressTime = Date.now();
      
      // Rastrear progreso de carga
      xhr.upload.addEventListener('progress', (e) => {
        lastProgressTime = Date.now(); // Reiniciar contador de inactividad
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percentComplete);
          
          if (percentComplete < 100) {
            setPostMessage(`Subiendo archivos: ${percentComplete}%`);
          } else {
            setPostMessage("Procesando en el servidor...");
          }
        }
      });

      // Rastrear cuando inicia la descarga (el servidor está procesando)
      xhr.addEventListener('loadstart', () => {
        setPostMessage("Conectando con servidor...");
        lastProgressTime = Date.now();
      });

      // Esperar respuesta
      const xhrPromise = new Promise((resolve, reject) => {
        let timeoutId;
        let inactivityTimeoutId;
        
        // Limpieza de timeouts
        const clearAllTimeouts = () => {
          clearTimeout(timeoutId);
          clearTimeout(inactivityTimeoutId);
        };
        
        xhr.addEventListener('load', () => {
          clearAllTimeouts();
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (e) {
              reject(new Error("Error al procesar respuesta del servidor"));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.error || errorData.message || "Error al publicar"));
            } catch (e) {
              reject(new Error("Error al publicar"));
            }
          }
        });

        xhr.addEventListener('error', () => {
          clearAllTimeouts();
          reject(new Error("Error de conexión"));
        });

        xhr.addEventListener('abort', () => {
          clearAllTimeouts();
          reject(new Error("Subida cancelada"));
        });

        // Timeout principal (muy generoso)
        timeoutId = setTimeout(() => {
          xhr.abort();
          reject(new Error("Tiempo de espera agotado (excedió " + Math.round(requestTimeoutMs / 1000) + " segundos)"));
        }, requestTimeoutMs);
        
        // Timeout de inactividad: si no hay progreso en 2 minutos, abortar
        inactivityTimeoutId = setInterval(() => {
          const timeSinceLastProgress = Date.now() - lastProgressTime;
          const inactivityThreshold = 120000; // 2 minutos sin progreso
          
          if (timeSinceLastProgress > inactivityThreshold) {
            clearAllTimeouts();
            xhr.abort();
            reject(new Error("La subida se detuvo. Intenta nuevamente."));
          }
        }, 30000); // Verificar cada 30 segundos

        xhr.open('POST', `${API_BASE_URL}/api/posts`);
        const token = localStorage.getItem('token');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(fd);
      });

      const createdPost = await xhrPromise;

      setPostMessage("Procesando menciones...");

      // Procesar menciones (no bloquear si falla)
      if (input.includes("@")) {
        try {
          await fetch(`${API_BASE_URL}/api/mentions/notify`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeader() },
            body: JSON.stringify({
              content: input,
              authorName: userInfo.name,
              authorId: userInfo.id,
              spaceId: selectedSpace,
              spaceName: spaces.find(s => s.id === Number(selectedSpace))?.name || "ASSA ABLOY"
            })
          });
        } catch (mentionError) {
          console.error("Error procesando menciones:", mentionError);
        }
      }

      setPostStatus("success");
      setPostMessage("¡Publicación creada exitosamente!");
      setUploadProgress(0);
      setIsPosting(false);
      setInput("");
      setFiles([]);
      setVideosLoaded({});
      setShowMentionSuggestions(false);
      setMentionSuggestions([]);
      setMentionQuery("");
      setMentionStart(null);

      window.dispatchEvent(
        new CustomEvent("post:created", {
          detail: {
            id: createdPost?.id,
            spaceId: Number(selectedSpace),
          },
        })
      );

      // Limpiar mensaje de éxito sin recargar la página
      setTimeout(() => {
        setPostStatus("");
        setPostMessage("");
      }, 1500);

    } catch (err) {
      console.error("Error al publicar:", err);
      setIsPosting(false);
      setPostStatus("error");
      setUploadProgress(0);

      if (err.message.includes("Tiempo de espera")) {
        setPostMessage("La subida tardó demasiado para completarse. Intenta con mejor conexión o vuelve a intentar en unos minutos.");
      } else if (err.message.includes("de conexión")) {
        setPostMessage("Error de conexión. Verifica que el servidor esté activo y tu conexión a internet.");
      } else if (err.message.includes("cancelada")) {
        setPostMessage("Subida cancelada.");
      } else {
        setPostMessage(err.message || "Error al publicar. Intenta nuevamente.");
      }

      setTimeout(() => {
        setPostStatus("");
        setPostMessage("");
      }, 5000);
    }
  };

  // 4) Emoji
  const addEmoji = e => {
    const codes = e.unified.split("-").map(u => "0x" + u);
    setInput(i => i + String.fromCodePoint(...codes));
  };

  // 5) Archivos adjuntos
  const removeFile = idx => {
    setFiles(list => list.filter((_, i) => i !== idx));
    // Actualizar el estado de videos cargados
    setVideosLoaded(prev => {
      const newState = { ...prev };
      delete newState[idx];
      // Reindexar los videos restantes
      const reindexed = {};
      Object.keys(newState).forEach(key => {
        const numKey = parseInt(key);
        if (numKey > idx) {
          reindexed[numKey - 1] = newState[key];
        } else {
          reindexed[numKey] = newState[key];
        }
      });
      return reindexed;
    });
  };

  const extractMentionContext = (text, cursorIndex) => {
    const beforeCursor = text.slice(0, cursorIndex);
    const mentionMatch = beforeCursor.match(/(^|\s)@([a-zA-ZÀ-ÿ0-9._\-\s]{0,30})$/);
    if (!mentionMatch) return null;

    const matchedRaw = mentionMatch[0];
    const query = mentionMatch[2] || "";
    const start = cursorIndex - matchedRaw.length + matchedRaw.indexOf("@");
    return { start, query: query.trimStart() };
  };

  const updateMentionSuggestions = (text, cursorIndex) => {
    const mentionContext = extractMentionContext(text, cursorIndex);

    if (!mentionContext) {
      setShowMentionSuggestions(false);
      setMentionSuggestions([]);
      setMentionStart(null);
      setMentionQuery("");
      return;
    }

    const queryLower = mentionContext.query.toLowerCase();
    const matches = allUsers
      .filter((u) => u.id !== currentUser?.id)
      .filter((u) => {
        const byName = (u.name || "").toLowerCase().includes(queryLower);
        const byUsername = (u.username || "").toLowerCase().includes(queryLower);
        return byName || byUsername;
      })
      .slice(0, 6);

    setMentionStart(mentionContext.start);
    setMentionQuery(mentionContext.query);
    setMentionSuggestions(matches);
    setShowMentionSuggestions(matches.length > 0);
  };

  const applyMention = (user) => {
    if (!textareaRef.current || mentionStart === null) return;

    const cursor = textareaRef.current.selectionStart;
    const mentionValue = user.name || user.username;
    const newText = `${input.slice(0, mentionStart)}@${mentionValue} ${input.slice(cursor)}`;

    setInput(newText);
    setShowMentionSuggestions(false);
    setMentionSuggestions([]);
    setMentionQuery("");
    setMentionStart(null);
    setMentionNotice(`${mentionValue} será notificado al publicar.`);

    setTimeout(() => setMentionNotice(""), 2200);

    requestAnimationFrame(() => {
      const nextCursor = mentionStart + mentionValue.length + 2;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const renderHighlighted = (value) => {
    if (!mentionQuery) return value;
    const index = value.toLowerCase().indexOf(mentionQuery.toLowerCase());
    if (index === -1) return value;

    return (
      <>
        {value.slice(0, index)}
        <span style={{ color: "#0077c8", fontWeight: 700 }}>{value.slice(index, index + mentionQuery.length)}</span>
        {value.slice(index + mentionQuery.length)}
      </>
    );
  };

  // 6) Verificar si hay archivos cargando (imágenes o videos)
  const hasMediaLoading = () => {
    if (files.length === 0) return false;
    
    return files.some((file, idx) => {
      // Los archivos están listos cuando videosLoaded[idx] es true
      // O cuando son archivos pequeños que se cargan rápido
      const isSmallFile = file.size < (2 * 1024 * 1024); // Menos de 2MB
      return videosLoaded[idx] !== true && !isSmallFile;
    });
  };

  // 7) Render
  return (
    <div className={`share ${darkMode ? "dark" : ""}`}>      {/* Overlay de estado de publicación */}
      {postStatus && (
        <div className={`postStatusOverlay ${postStatus}`}>
          <div className="postStatusModal">
            {postStatus === "loading" && (
              <>
                <div className="spinnerLoader"></div>
                <p className="statusMessage">{postMessage}</p>
                {uploadProgress > 0 && (
                  <div className="progressContainer">
                    <div className="progressBar">
                      <div 
                        className="progressFill" 
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="progressText">{uploadProgress}%</p>
                  </div>
                )}
              </>
            )}
            {postStatus === "success" && (
              <>
                <div className="successIcon">✓</div>
                <p className="statusMessage">{postMessage}</p>
              </>
            )}
            {postStatus === "warning" && (
              <>
                <div className="warningIcon" aria-hidden="true" />
                <p className="statusMessage">{postMessage}</p>
              </>
            )}
            {postStatus === "error" && (
              <>
                <div className="errorIcon">✕</div>
                <p className="statusMessage">{postMessage}</p>
              </>
            )}
          </div>
        </div>
      )}
      <div className="shareWrapper">
        {/* Top */}
        <div className="shareTop">
          <img
            className="shareProfileImg"
            src={urlDeArchivo(userInfo.profilePicture) || "/assets/profileCover/DefaultProfile.jpg"}
            alt={userInfo.name}
          />
          <div className="shareInputWrapper">
            <div
              ref={mirrorRef}
              className={`shareInputMirror ${input ? "hasContent" : "isPlaceholder"}`}
              aria-hidden="true"
            >
              {input ? renderFormattedText(input) : `¿Qué tienes en mente, ${userInfo.name}?`}
            </div>

            <textarea
              ref={textareaRef}
              className="shareInput shareInputOverlay"
              rows={2}
              placeholder={`¿Qué tienes en mente, ${userInfo.name}?`}
              value={input}
              onChange={e => {
                const value = e.target.value;
                setInput(value);
                updateMentionSuggestions(value, e.target.selectionStart);
              }}
              onScroll={(e) => {
                if (mirrorRef.current) {
                  mirrorRef.current.scrollTop = e.currentTarget.scrollTop;
                  mirrorRef.current.scrollLeft = e.currentTarget.scrollLeft;
                }
              }}
              onClick={(e) => updateMentionSuggestions(input, e.target.selectionStart)}
              onKeyUp={(e) => updateMentionSuggestions(e.currentTarget.value, e.currentTarget.selectionStart)}
              onBlur={() => setTimeout(() => setShowMentionSuggestions(false), 120)}
              onKeyDown={e => {
                if (showMentionSuggestions && mentionSuggestions.length > 0 && e.key === "Enter") {
                  e.preventDefault();
                  applyMention(mentionSuggestions[0]);
                  return;
                }

                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePost(e);
                }
              }}
            />

            {showMentionSuggestions && (
              <div className="mentionSuggestions">
                {mentionSuggestions.map((user) => (
                  <button
                    type="button"
                    key={user.id}
                    className="mentionSuggestionItem"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      applyMention(user);
                    }}
                  >
                    <img
                      src={urlDeArchivo(user.profilePicture) || "/assets/profileCover/DefaultProfile.jpg"}
                      alt={user.name || user.username}
                    />
                    <div className="mentionSuggestionText">
                      <span>{renderHighlighted(user.name || "")}</span>
                      <small>@{renderHighlighted(user.username || "")}</small>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {mentionNotice && <div className="mentionNotice">{mentionNotice}</div>}
        <hr className="shareHr" />

        {/* Previsualización de medios */}
        {files.length > 0 && (
          <div className="mediaPreviewList">
            {files.map((f, i) => (
              <div key={i} className="mediaPreviewItem">
                {f.type.startsWith("image/") ? (
                  <div className="imagePreviewWrapper">
                    <img 
                      src={URL.createObjectURL(f)} 
                      alt="preview"
                      onLoad={() => {
                        setVideosLoaded(prev => ({ ...prev, [i]: true }));
                      }}
                      onError={() => {
                        setVideosLoaded(prev => ({ ...prev, [i]: false }));
                      }}
                    />
                    {videosLoaded[i] !== true && (
                      <div className="mediaLoadingOverlay">
                        <div className="mediaSpinner"></div>
                        <span>Cargando imagen...</span>
                      </div>
                    )}
                    {videosLoaded[i] === true && (
                      <div className="mediaReadyBadge">
                        <span className="readyCheckmark">✓</span>
                        <span className="readyText">Listo</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="videoPreviewWrapper">
                    <video 
                      src={URL.createObjectURL(f)} 
                      controls
                      controlsList="nodownload"
                      onCanPlayThrough={() => {
                        setVideosLoaded(prev => ({ ...prev, [i]: true }));
                      }}
                      onCanPlay={() => {
                        // También marcar como listo si puede jugar (más flexible que onCanPlayThrough)
                        setVideosLoaded(prev => {
                          if (prev[i] !== true) {
                            return { ...prev, [i]: true };
                          }
                          return prev;
                        });
                      }}
                      onLoadedMetadata={() => {
                        // Marcar como listo cuando al menos los metadatos estén cargados
                        setVideosLoaded(prev => {
                          if (prev[i] !== true) {
                            return { ...prev, [i]: true };
                          }
                          return prev;
                        });
                      }}
                      onError={() => {
                        setVideosLoaded(prev => ({ ...prev, [i]: false }));
                      }}
                    />
                    {videosLoaded[i] !== true && (
                      <div className="mediaLoadingOverlay">
                        <div className="mediaSpinner"></div>
                        <span>Cargando video...</span>
                      </div>
                    )}
                    {videosLoaded[i] === true && (
                      <div className="mediaReadyBadge">
                        <span className="readyCheckmark">✓</span>
                        <span className="readyText">Listo</span>
                      </div>
                    )}
                  </div>
                )}
                <button
                  className="removeMedia"
                  onClick={() => removeFile(i)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Bottom */}
        <div className="shareBottom">
          <div className="shareOptions">
            {/* Adjuntar media */}
            <label htmlFor="file" className="shareOption">
              <PermMedia style={{ color: "#2e0196f1" }} />
              <span>Imagen/Video</span>
              <input
                type="file"
                id="file"
                multiple
                accept="image/*,video/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const selectedFiles = Array.from(e.target.files);
                  const validFiles = [];

                  selectedFiles.forEach((file) => {
                    const isImage = file.type.startsWith("image/");
                    const isVideo = file.type.startsWith("video/");
                    const sizeMB = file.size / (1024 * 1024);

                    if (isImage && sizeMB > 5) {
                      alert(`La imagen "${file.name}" supera los 5 MB y no será subida.`);
                    } else if (isVideo && sizeMB > 1024) {
                      alert(`El video "${file.name}" supera 1 GB y no será subido.`);
                    } else {
                      validFiles.push(file);
                    }
                  });

                  // Inicializar estado de carga para todos los archivos (imágenes y videos)
                  const currentLength = files.length;
                  const newMediaState = {};
                  validFiles.forEach((file, idx) => {
                    newMediaState[currentLength + idx] = false; // No cargado aún
                  });
                  setVideosLoaded(prev => ({ ...prev, ...newMediaState }));
                  
                  setFiles((prev) => [...prev, ...validFiles]);
                  e.target.value = "";
                }}
              />
            </label>

            {/* Emoji */}
            <div
              className="shareOption"
              onClick={() => setShowEmojis(v => !v)}
            >
              <EmojiEmotions style={{ color: "#bfc600ec" }} />
              <span>Emoji</span>
            </div>

            {/* Encuesta: se crea junto con la publicación, no como un
                botón aparte flotando fuera del recuadro de "Compartir". */}
            <div
              className="shareOption"
              onClick={() => setShowCreatePoll(true)}
            >
              <Addchart style={{ color: "#0077c8" }} />
              <span>Encuesta</span>
            </div>

            {/* Selector visual de espacios: administradores y encargados */}
            {muestraSelector && (
              <div className="spaceSelectWrapper">
                <label>Publicar en:</label>
                <div className="spaceList">
                  {espaciosParaPublicar.map(s => (
                    <div
                      key={s.id}
                      className={
                        "spaceItem" +
                        (String(selectedSpace) === String(s.id) ? " selected" : "")
                      }
                      onClick={() => setSelectedSpace(s.id)}
                    >
                      {s.image ? (
                        <img src={urlDeArchivo(s.image)} alt={s.name} />
                      ) : (
                        <span className="fallbackIcon">SP</span>
                      )}
                      <span>{s.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button 
            type="button"
            className="shareButton" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePost(e);
            }}
            disabled={isPosting}
            title={isPosting ? "Publicando..." : "Publicar"}
            style={{ 
              opacity: isPosting ? 0.5 : 1, 
              cursor: isPosting ? 'not-allowed' : 'pointer',
              position: 'relative'
            }}
          >
            <img src={sendIcon} alt="send" className="sendIcon" />
          </button>
        </div>

        {/* Emoji Picker */}
        {showEmojis && (
          <div className="emoji">
            <Picker data={data} onEmojiSelect={addEmoji} />
          </div>
        )}

        {/* Modal de encuesta: por defecto preselecciona el mismo espacio
            que se tiene elegido para publicar, pero se puede cambiar
            dentro del propio modal. */}
        {showCreatePoll && (
          <CreatePollModal
            spaceId={selectedSpace}
            onClose={() => setShowCreatePoll(false)}
            onCreated={(poll) => {
              window.dispatchEvent(new CustomEvent("poll:created", { detail: poll }));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Share;
